/**
 * @file bookingSidebar.component.tsx
 * @path apps/website/src/features/booking/components/layout/bookingSidebar.component.tsx
 * @description Compact itinerary sidebar — rewired to Zustand store.
 */

import { CompletedBooking } from "@sirbastians/types";
import { Button, Card } from "@sirbastians/ui";
import { cn, formatCurrency } from "@sirbastians/utils";
import { format } from "date-fns";
import {
  AlarmFill,
  CalendarDateFill,
  CarFrontFill,
  CheckCircle,
  ChevronDown,
  Geo,
  GeoAltFill,
  InfoCircleFill,
  LuggageFill,
  PencilFill,
  PeopleFill,
} from "react-bootstrap-icons";
import { useState } from "react";
import { useBookingStore } from "../../booking.store";
import { useBookingPricing } from "../../hooks/useBookingPricing.hook";
import { Sofa } from "lucide-react";

// --- Helpers ---

const SummaryItem = ({
  icon: Icon,
  label,
  value,
  isExtra = false,
}: {
  icon: React.ElementType;
  label: string;
  value?: React.ReactNode;
  isExtra?: boolean;
}) => (
  <div className="flex items-center justify-between text-sm gap-2">
    <div className="flex items-center text-muted-foreground min-w-0">
      <Icon className="h-3.5 w-3.5 mr-2 flex-shrink-0 opacity-70" />
      <span className="truncate">{label}</span>
    </div>
    {value && (
      <span
        className={cn(
          "font-medium text-right shrink-0",
          isExtra ? "text-green-600" : "text-foreground",
        )}
      >
        {value}
      </span>
    )}
  </div>
);

const SummarySection = ({
  title,
  children,
  onEdit,
}: {
  title: string;
  children: React.ReactNode;
  onEdit?: () => void;
}) => (
  <div className="pb-4 mb-4 border-b border-dashed border-border last:border-b-0 last:pb-0 last:mb-0">
    <div className="flex justify-between items-center mb-2.5">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h4>
      {onEdit && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="h-6 w-6 p-0 hover:bg-transparent text-accent hover:text-accent/80"
        >
          <PencilFill className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

// --- Completed booking one-liner ---

const CompletedBookingRow = ({
  booking,
  index,
  isExpanded,
  onToggle,
}: {
  booking: CompletedBooking;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}) => (
  <div className="border-b border-dashed border-border last:border-b-0 pb-2 mb-2 last:pb-0 last:mb-0">
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center justify-between w-full text-left gap-2"
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="flex items-center justify-center h-4 w-4 rounded-full bg-accent/10 text-accent text-[9px] font-bold shrink-0">
          {index + 1}
        </span>
        <span className="text-xs font-medium truncate">
          {booking.selectedService.title}
        </span>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <span className="text-xs font-semibold">
          {formatCurrency(booking.pricing.total)}
        </span>
        <ChevronDown
          className={cn(
            "h-3 w-3 text-muted-foreground transition-transform",
            isExpanded && "rotate-180",
          )}
        />
      </div>
    </button>

    {isExpanded && (
      <div className="mt-1.5 pl-5.5 space-y-0.5 text-xs text-muted-foreground">
        <p>
          {format(booking.bookingDate, "dd MMM yyyy")} · {booking.selectedTime}
        </p>
        <p>
          {booking.adultCount}A{" "}
          {booking.childCount > 0 ? `${booking.childCount}C ` : ""}
          {booking.infantCount > 0 ? `${booking.infantCount}I` : ""}
        </p>
      </div>
    )}
  </div>
);

interface BookingSidebarProps {
  onGoToStep?: (stepId: string) => void;
}

export const BookingSidebar = ({ onGoToStep }: BookingSidebarProps) => {
  const store = useBookingStore();
  const { pricing, grandTotal } = useBookingPricing();

  const {
    selectedService,
    includedActivities,
    selectedActivities,
    selectedDate,
    routeStops,
    selectedVehicles,
    babySeats,
    trailerNeeded,
    completedBookings,
    adultsCount,
    childrenCount,
    infantsCount,
  } = store;

  const [expandedBookingId, setExpandedBookingId] = useState<string | null>(
    null,
  );

  const payingPassengers = adultsCount + childrenCount;

  if (!selectedService && completedBookings.length === 0) {
    return (
      <Card className="rounded-xl border-dashed">
        <div className="p-6 text-center text-muted-foreground">
          <InfoCircleFill className="mx-auto h-8 w-8 mb-2 opacity-20" />
          <h4 className="font-semibold text-foreground text-sm mb-0.5">
            Itinerary Empty
          </h4>
          <p className="text-xs">Select a service to start planning.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl overflow-hidden border-border flex flex-col max-h-[calc(100vh-8rem)]">
      {/* Scrollable content */}
      <div className="p-4 flex-grow overflow-y-auto">
        <h3 className="text-base font-bold text-foreground mb-4 sticky top-0 bg-card z-10 pb-1.5">
          Your Itinerary
        </h3>

        {/* Completed bookings (collapsed rows) */}
        {completedBookings.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">
              Saved Bookings ({completedBookings.length})
            </h4>
            {completedBookings.map((b, i) => (
              <CompletedBookingRow
                key={b.id}
                booking={b}
                index={i}
                isExpanded={expandedBookingId === b.id}
                onToggle={() =>
                  setExpandedBookingId(expandedBookingId === b.id ? null : b.id)
                }
              />
            ))}
          </div>
        )}

        {/* Current draft */}
        {selectedService && (
          <div className="animate-in fade-in slide-in-from-bottom-2">
            {completedBookings.length > 0 && (
              <div className="text-xs font-semibold text-accent uppercase tracking-wider mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                Current Draft
              </div>
            )}

            <SummarySection
              title="Service"
              onEdit={() => onGoToStep?.("service")}
            >
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm text-foreground truncate">
                    {selectedService.title}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {selectedService.category}
                  </div>
                </div>
                <span className="font-bold text-sm shrink-0">
                  {formatCurrency(pricing.servicePrice)}
                </span>
              </div>
            </SummarySection>

            {/* Activities */}
            {(includedActivities.length > 0 ||
              selectedActivities.length > 0) && (
              <SummarySection
                title="Activities"
                onEdit={() => onGoToStep?.("activities")}
              >
                {selectedActivities.map((a) => (
                  <SummaryItem
                    key={a.id}
                    icon={GeoAltFill}
                    label={a.title}
                    value={`+${formatCurrency(a.pricing.amount * payingPassengers)}`}
                    isExtra
                  />
                ))}
                {includedActivities.map((a) => (
                  <SummaryItem
                    key={a.id}
                    icon={CheckCircle}
                    label={a.title}
                    value="Included"
                  />
                ))}
              </SummarySection>
            )}

            {/* Trip details */}
            <SummarySection
              title="Trip Details"
              onEdit={() => onGoToStep?.("trip-details")}
            >
              <SummaryItem
                icon={PeopleFill}
                label={`Adults (${adultsCount})`}
              />
              {childrenCount > 0 && (
                <SummaryItem
                  icon={PeopleFill}
                  label={`Children (${childrenCount})`}
                />
              )}
              {infantsCount > 0 && (
                <SummaryItem
                  icon={PeopleFill}
                  label={`Infants (${infantsCount})`}
                  value="Free"
                />
              )}
              {babySeats > 0 && (
                <SummaryItem
                  icon={Sofa}
                  label={`Baby Seats (${babySeats})`}
                  value={`+${formatCurrency(babySeats * 350)}`}
                  isExtra
                />
              )}
              {trailerNeeded && (
                <SummaryItem
                  icon={LuggageFill}
                  label="Luggage Trailer"
                  value={`+${formatCurrency(450)}`}
                  isExtra
                />
              )}
              {pricing.extraStopsPrice > 0 && (
                <SummaryItem
                  icon={GeoAltFill}
                  label="Extra Stops"
                  value={`+${formatCurrency(pricing.extraStopsPrice)}`}
                  isExtra
                />
              )}
              {pricing.vehiclesPrice > 0 &&
                selectedVehicles.map((v) => {
                  if (v.type === "standard") return null;
                  const vip = selectedService.vipOptions?.find(
                    (o: {
                      id: string;
                      price: number;
                      label?: string;
                      name?: string;
                    }) => o.id === v.id,
                  );
                  return vip ? (
                    <SummaryItem
                      key={v.id}
                      icon={CarFrontFill}
                      label={`${(vip as { label?: string; name?: string }).label || (vip as { name?: string }).name} Upgrade`}
                      value={`+${formatCurrency(vip.price * v.count)}`}
                      isExtra
                    />
                  ) : null;
                })}
            </SummarySection>

            {/* Schedule */}
            {selectedDate && (
              <SummarySection
                title="Schedule"
                onEdit={() => onGoToStep?.("schedule-route")}
              >
                <SummaryItem
                  icon={CalendarDateFill}
                  label="Date"
                  value={format(selectedDate, "dd MMM yyyy")}
                />
                <SummaryItem
                  icon={AlarmFill}
                  label="Time"
                  value={
                    store.selectedTime || (
                      <span className="text-muted-foreground italic text-[10px]">
                        Not set
                      </span>
                    )
                  }
                />
              </SummarySection>
            )}
          </div>
        )}
      </div>

      {/* Grand total — always visible at bottom */}
      <div className="bg-muted/50 px-4 py-3 border-t border-border mt-auto">
        <div className="flex justify-between items-end">
          <span className="text-muted-foreground font-medium text-xs uppercase tracking-wide">
            Grand Total
          </span>
          <span className="text-xl font-bold text-accent">
            {formatCurrency(grandTotal)}
          </span>
        </div>
      </div>
    </Card>
  );
};
